import {
    PsHttpService,
    type PatSnapCommonResponseData,
    PsHttpError,
    isPatSnapErrorResponseData,
} from '@patsnap/tahiti-core/common/ps-http';
import type { RequestConfig } from '@patsnap/common-ps-http';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import { merge } from 'lodash';
import type { SkillApiService } from '../skill-api';
import type {
    WebSkillListParams,
    WebSkillListResponse,
    WebSkillDetailItem,
    WebSkillCreateRequest,
    WebSkillCreateData,
    WebSkillEnableData,
    WebSkillDeleteData,
    WebSkillUpdateRequest,
    WebSkillUpdateData,
    WebSkillImportRequest,
    WebSkillImportData,
} from '../../types';

@injectable()
export class SkillApiServiceImpl extends PsHttpService implements SkillApiService {
    get options() {
        return {
            baseURL: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/hub-api/web/skill`,
            headers: {
                'X-API-Version': '1.0',
            },
        };
    }

    // 覆盖 request 方法以处理后端返回 data.data 为 null 的情况
    override request<T extends PatSnapCommonResponseData>(config: RequestConfig): Promise<T> {
        config.headers = merge({}, this.options.headers, config.headers);
        config.baseURL = config.baseURL || this.options.baseURL;
        return this.httpClient<T>(config)
            .then((res) => {
                const data = res.data;
                if (isPatSnapErrorResponseData(data)) {
                    // 处理 data.data 为 null 的情况，使用 error_msg 作为备选
                    const errorMessage = data.data?.error_message ?? data.error_msg ?? 'Unknown error';
                    const error = new PsHttpError(errorMessage, {
                        cause: data,
                    });
                    throw error;
                }
                return res.data;
            });
    }

    getList(params?: WebSkillListParams) {
        return this.get<PatSnapCommonResponseData<WebSkillListResponse>>({
            // url: '/list',
            url: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/api/web/skill/list`,
            params,
        }).then(res => res.data as WebSkillListResponse);
    }

    getDetail(id: string) {
        return this.get<PatSnapCommonResponseData<WebSkillDetailItem>>({
            url: `/by-id/${encodeURIComponent(id)}`,
        }).then(res => res.data as WebSkillDetailItem);
    }

    create(data: WebSkillCreateRequest) {
        return this.post<PatSnapCommonResponseData<WebSkillCreateData>>({
            url: '/create',
            data,
        }).then(res => res.data as WebSkillCreateData);
    }

    update(id: string, data: WebSkillUpdateRequest) {
        return this.request<PatSnapCommonResponseData<WebSkillUpdateData>>({
            url: `/by-id/${encodeURIComponent(id)}`,
            method: 'PUT',
            data,
        }).then(res => res.data as WebSkillUpdateData);
    }

    enable(id: string) {
        return this.post<PatSnapCommonResponseData<WebSkillEnableData>>({
            url: `/by-id/${encodeURIComponent(id)}/enable`,
        }).then(res => res.data as WebSkillEnableData);
    }

    disable(id: string) {
        return this.post<PatSnapCommonResponseData<WebSkillEnableData>>({
            url: `/by-id/${encodeURIComponent(id)}/disable`,
        }).then(res => res.data as WebSkillEnableData);
    }

    remove(id: string) {
        return this.request<PatSnapCommonResponseData<WebSkillDeleteData>>({
            url: `/by-id/${encodeURIComponent(id)}`,
            method: 'DELETE',
        }).then(res => res.data as WebSkillDeleteData);
    }

    exportSkill(id: string) {
        return this.request<Blob>({
            url: `/by-id/${encodeURIComponent(id)}/export`,
            method: 'GET',
            responseType: 'blob',
        });
    }

    importSkill(data: WebSkillImportRequest) {
        const formData = new FormData();
        formData.append('file', data.file);
        if (data.name) {
            formData.append('name', data.name);
        }
        if (data.overwrite !== undefined) {
            formData.append('overwrite', String(data.overwrite));
        }
        return this.post<PatSnapCommonResponseData<WebSkillImportData>>({
            url: '/import',
            data: formData,
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }).then(res => res.data as WebSkillImportData);
    }
}
